import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/lib/content";
import { useConsult } from "@/App";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/audit", label: "Free Audit" },
  { to: "/about", label: "About" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openConsult } = useConsult();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 z-50 w-full border-b backdrop-blur-xl transition-colors duration-500 ${
        scrolled ? "border-gold/20 bg-navy/85" : "border-gold/10 bg-navy/60"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={IMAGES.logo} alt="Elite Imperium Digital" className="h-11 w-auto rounded-sm object-cover" />
        </Link>

        <nav className="hidden items-center gap-7 xl:flex" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                  isActive ? "text-gold-glow" : "text-ivory/70 hover:text-ivory"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={openConsult}
            data-testid="nav-book-consultation-btn"
            className="group hidden items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-[13px] font-semibold text-navy-deep transition-colors duration-300 hover:bg-gold-light sm:flex"
          >
            Book Consultation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-btn"
            className="rounded-lg border border-gold/20 p-2.5 text-ivory xl:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-gold/10 bg-navy/95 backdrop-blur-xl xl:hidden"
            data-testid="nav-mobile"
          >
            <div className="space-y-1 px-6 py-6">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `block rounded-lg px-4 py-3 font-serif text-xl ${isActive ? "text-gold-glow" : "text-ivory/80"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                onClick={() => { setOpen(false); openConsult(); }}
                data-testid="nav-mobile-book-btn"
                className="mt-4 w-full rounded-full bg-gold px-5 py-3.5 text-sm font-semibold text-navy-deep"
              >
                Book a Free Consultation
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
